import { z } from 'zod';

/**
 * Contact timeline wire schemas — the shape Middleman's
 * `resolve-contact-timeline` returns per contact, served by
 * GET /api/flow/contacts/:contactId/timeline.
 *
 * Adapted from action-view's schemas (mini-apps/action-view/types.ts) so the
 * core Flow surface has no mini-app dependency; action-view keeps its own
 * copy (mini-apps cannot be imported from core).
 */

/**
 * `rowLimitPerObject` the handler sends to Middleman's
 * `resolve-contact-timeline` (src/api/flow.ts). It lives on the shared wire
 * contract, not at either call site, because BOTH sides need it: the request
 * sets it, and the frontend's "+more workspace memberships may exist" notice
 * (ContactTimeline.tsx) fires on an exact-N return — the only signal that the
 * cap, rather than reality, ended the list. Two independent literals would
 * drift the moment one is bumped, silently turning that notice into either a
 * permanent false negative or a permanent false positive.
 */
export const CONTACT_TIMELINE_ROW_LIMIT_PER_OBJECT = 50;

export const FlowContactTouchpointSchema = z.object({
  touchpointId: z.string(),
  contactId: z.string(),
  type: z.string().nullable(),
  touchpointDate: z.string().nullable(),
  touchpointDetail: z.string().nullable(),
  description: z.string().nullable(),
  aiReferralSourceAnswer: z.string().nullable(),
  createdAt: z.string().nullable(),
  lastModifiedAt: z.string().nullable(),
});

export const FlowContactCampaignMembershipSchema = z.object({
  campaignMemberId: z.string(),
  contactId: z.string(),
  campaignName: z.string().nullable(),
  campaignType: z.string().nullable(),
  status: z.string().nullable(),
  description: z.string().nullable(),
  /**
   * The rep's Campaign_Member_Notes__c, decorated server-side from the
   * `salesforce_signal_action` mirror (the live CampaignMember SOQL does not
   * select it). Absent when the contact has no note for this membership.
   */
  campaignMemberNotes: z.string().nullable().optional(),
  createdDate: z.string().nullable(),
  lastModifiedAt: z.string().nullable(),
});

export const FlowContactTaskSchema = z.object({
  taskId: z.string(),
  contactId: z.string(),
  subject: z.string().nullable(),
  description: z.string().nullable(),
  type: z.string().nullable(),
  subtype: z.string().nullable(),
  status: z.string().nullable(),
  priority: z.string().nullable(),
  isClosed: z.boolean().nullable(),
  activityDate: z.string().nullable(),
  gongParticipantsEmails: z.string().nullable(),
  ownerId: z.string().nullable(),
  ownerName: z.string().nullable(),
  ownerEmail: z.string().nullable(),
  createdAt: z.string().nullable(),
  lastModifiedAt: z.string().nullable(),
});

export const FlowContactEventSchema = z.object({
  eventId: z.string(),
  contactId: z.string(),
  subject: z.string().nullable(),
  description: z.string().nullable(),
  type: z.string().nullable(),
  subtype: z.string().nullable(),
  location: z.string().nullable(),
  isAllDay: z.boolean().nullable(),
  startDateTime: z.string().nullable(),
  endDateTime: z.string().nullable(),
  ownerId: z.string().nullable(),
  ownerName: z.string().nullable(),
  ownerEmail: z.string().nullable(),
  attendees: z.array(z.object({
    relationId: z.string(),
    name: z.string().nullable(),
    type: z.string(),
  })).default([]),
  createdAt: z.string().nullable(),
  lastModifiedAt: z.string().nullable(),
});

export const FlowContactWorkspaceUserSchema = z.object({
  workspaceUserId: z.string(),
  contactId: z.string(),
  relationshipType: z.string().nullable(),
  workspaceCreatedAt: z.string().nullable(),
  workspaceName: z.string().nullable(),
  lastModifiedAt: z.string().nullable(),
  // ── terra-middleware PR #254 (merged 2026-08-25) — per-contact workspace
  // identity fields from `resolve-contact-timeline`'s `Workspace__r`
  // traversal. `.nullable().optional()` (rather than a bare `.nullable()`
  // or `.optional().default(null)`) so: (a) a pre-PR-#254 Middleman
  // response — missing these keys entirely — still parses without
  // throwing (Decision 9's tolerant-of-absence rule), and (b) the
  // z.infer'd TS type keeps these keys optional rather than
  // required-with-a-filled-in-default, so existing fixtures/tests
  // constructed before this field existed keep type-checking. Call sites
  // read these with `?? null` at the point of use for the wire's `?? null`
  // semantics (same deployment-skew idiom as `SfTaskSchema.whatId` in
  // `src/services/salesforce/reads/schemas.ts`). ──
  /** `Workspace__r.Workspace_ID__c` — the Clay app workspace id. */
  workspaceId: z.string().nullable().optional(),
  /** `Workspace__r.Account__c` — the SF account id the workspace belongs
   *  to. Compared against the viewed contact's own account id to decide
   *  Main vs. Separate (never derive this from `Primary_Workspace_Link__c`
   *  — see the plan's Decision on the per-contact Main/Separate badge). */
  workspaceAccountId: z.string().nullable().optional(),
  /** `Workspace__r.Product_Tier__c`. */
  productTier: z.string().nullable().optional(),
  /** `Workspace__r.Active_Status__c`. */
  activeStatus: z.string().nullable().optional(),
  /** `Workspace__r.Active_Trial__c`. */
  activeTrial: z.boolean().nullable().optional(),
  /** `Workspace__r.Current_Subscription_ARR__c`. */
  subscriptionArr: z.number().nullable().optional(),
});

export const FlowContactSignalSchema = z.object({
  signalId: z.string(),
  contactId: z.string(),
  type: z.string().nullable(),
  subtype: z.string().nullable(),
  description: z.string().nullable(),
  eventDate: z.string().nullable(),
  createdAt: z.string().nullable(),
  lastModifiedAt: z.string().nullable(),
});

export const FlowContactTimelineSchema = z.object({
  touchpoints: z.array(FlowContactTouchpointSchema),
  campaignMemberships: z.array(FlowContactCampaignMembershipSchema),
  tasks: z.array(FlowContactTaskSchema),
  events: z.array(FlowContactEventSchema),
  workspaceUsers: z.array(FlowContactWorkspaceUserSchema),
  signals: z.array(FlowContactSignalSchema),
  truncated: z.record(z.string(), z.boolean()),
});
export type FlowContactTimeline = z.infer<typeof FlowContactTimelineSchema>;

export const EMPTY_CONTACT_TIMELINE: FlowContactTimeline = {
  touchpoints: [], campaignMemberships: [], tasks: [], events: [],
  workspaceUsers: [], signals: [], truncated: {},
};
